import { Link } from 'react-router-dom';
import { t, tn } from '@app/i18n';
import { dataHour } from '@app/format';
import { Alert, Badge, Button, ErrorBanner } from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import type { SiteConnectionTest } from './SiteModel';
import s from './Sites.module.css';

export interface SiteConnectionDialogProps {
  open: boolean;
  /** Nome do site testado, para o título. */
  site: string;
  /** A hora da última leitura do trust domain do site, para o «não lido» dizer desde quando (aim-p4qh). */
  trustDomainReadAt?: string | null;
  /** Resultado do teste; ausente enquanto a chamada não voltou. */
  result?: SiteConnectionTest;
  /** Falha de transporte — o teste nem chegou a responder. */
  error?: ApiError | null;
  pending?: boolean;
  onClose: () => void;
}

/**
 * O resultado do «Testar conexão» (P8 nº 3 — a *verificação executável*).
 *
 * # POR QUE TRÊS RESPOSTAS, E NÃO UM SEMÁFORO
 *
 * O contrato devolve **três afirmações independentes de propósito**: a sondagem
 * do executor, a validação do template e a conferência do trust domain. Fundi-las
 * num «ok / não ok» faria um cluster fora do ar ESCONDER uma colisão de ID — que é
 * justamente o problema que não aparece sozinho: ele só se manifesta quando dois
 * sites emitem o mesmo SPIFFE ID para workloads diferentes, e aí já há identidade
 * emitida. E faria um executor DE PÉ com o trust domain errado passar por verde.
 *
 * A validação do template roda **mesmo com o executor fora do ar**: ela é local,
 * e é a única coisa útil que se pode afirmar sobre um site que ainda não subiu.
 * É por isso que o bloco dela aparece sempre, e o da sondagem tem três estados.
 */
export function SiteConnectionDialog({
  open,
  site,
  trustDomainReadAt,
  result,
  error,
  pending = false,
  onClose,
}: SiteConnectionDialogProps) {
  if (!open) return null;

  return (
    <div className={s.overlay} data-testid="site-test-overlay">
      <div className={s.mbox} role="dialog" aria-modal="true" aria-labelledby="site-test-title">
        <div className={s.mh}>
          <h3 id="site-test-title">{t('sites.test.title', { name: site })}</h3>
        </div>

        <div className={s.mb}>
          {pending ? <p>{t('sites.test.running')}</p> : null}

          {error ? (
            // `hideLink`: o link corretivo da tabela de erros aponta para a
            // Infraestrutura, e quem esta a testar a conexao ja esta no lugar de
            // corrigir o endereco — o do site, aqui no drawer.
            <ErrorBanner error={error} title={t('sites.test.failed.title')} hideLink />
          ) : null}

          {result ? (
            <>
              <section className={s.testBlock}>
                <h4 className={s.testHead}>
                  {t('sites.test.probe.heading')} <ProbeBadge result={result} />
                </h4>
                <p className={s.testBody}>{probeText(result)}</p>
                {/*
                  A duração sai sempre, inclusive na falha: um `pdp_inalcancavel`
                  em 40 ms é recusa do outro lado; em 30 s é rede. São diagnósticos
                  diferentes, e o número é o que os separa.
                */}
                <p className={s.testMeta}>
                  {t('sites.test.probe.duration', { ms: result.duration_ms })}
                  {result.version
                    ? ` · ${t('sites.test.probe.version', { v: result.version })}`
                    : ''}
                </p>
              </section>

              <section className={s.testBlock}>
                <h4 className={s.testHead}>
                  {t('sites.test.template.heading')}{' '}
                  {result.template.collides_with.length === 0 ? (
                    <Badge tone="success">{t('sites.test.template.unique')}</Badge>
                  ) : (
                    <Badge tone="danger">{t('sites.test.template.collides')}</Badge>
                  )}
                </h4>

                <p className={s.testMono}>{result.template.template}</p>

                {result.template.collides_with.length > 0 ? (
                  <Alert tone="danger" title={t('sites.test.template.collides.title')}>
                    {tn(
                      'sites.test.template.collides.message',
                      result.template.collides_with.length,
                      {
                        sites: result.template.collides_with.join(', '),
                      },
                    )}
                  </Alert>
                ) : null}

                {result.template.deterministic ? (
                  <p className={s.testBody}>
                    {t('sites.test.template.preview', { id: result.template.preview })}
                  </p>
                ) : (
                  // Modo observado (P8.1): NÃO é erro, e o cadastro aceita. O texto
                  // diz o que trocar em vez de só «não é possível prever o ID».
                  <Alert tone="warning" title={t('sites.test.template.observed.title')}>
                    {t('sites.test.template.observed.message', {
                      placeholders: result.template.unknown_placeholders.join(', '),
                    })}
                  </Alert>
                )}
              </section>

              <TrustDomainBlock check={result.trust_domain} readAt={trustDomainReadAt} />
            </>
          ) : null}
        </div>

        <div className={s.mf}>
          <Button onClick={onClose}>{t('sites.test.close')}</Button>
        </div>
      </div>
    </div>
  );
}

/**
 * O terceiro bloco (S-b): o trust domain cadastrado × o que o SPIRE daquele
 * cluster reporta.
 *
 * # POR QUE ELE É UM BLOCO, E NÃO UMA LINHA DA SONDAGEM
 *
 * Pela mesma razão que a sondagem e o template já eram dois: um executor **de pé**
 * com o trust domain errado responde ao `/health` e emite SVIDs que o PDP não
 * reconhece. Pendurar a resposta na sondagem faria a tela dizer «respondeu» — que é
 * verdade, e é a informação errada.
 *
 * # «NÃO CONSULTADO» TEM BADGE PRÓPRIO
 *
 * `observed` nulo é executor fora do ar ou middleware anterior a AIG-03, que não
 * publica o campo. Desenhar ✓ ali afirmaria uma igualdade que ninguém verificou, e
 * desenhar ✗ acusaria divergência que ninguém viu — os dois em silêncio.
 */
function TrustDomainBlock({
  check,
  readAt,
}: {
  check: SiteConnectionTest['trust_domain'];
  readAt?: string | null;
}) {
  // O valor em USO depois do teste: o lido, quando houve leitura (o teste o gravou), e o de antes, quando não.
  const inUse = check.observed ?? check.expected;
  const when = dataHour(readAt);

  return (
    <section className={s.testBlock}>
      <h4 className={s.testHead}>
        {t('sites.test.trustDomain.heading')} <TrustDomainBadge check={check} />
      </h4>

      {/* O valor é DADO: sai em mono, como o template, nunca embutido numa frase. */}
      {inUse ? <p className={s.testMono}>{inUse}</p> : null}

      {check.matches ? (
        <p className={s.testBody}>
          {t('sites.test.trustDomain.matches.message', { observed: check.observed ?? '' })}
        </p>
      ) : null}

      {/* aim-p4qh: o teste GRAVOU o valor lido, com a linha na trilha. Os SPIFFE IDs previstos mudam com ele. */}
      {check.updated ? (
        <Alert tone="warning" title={t('sites.test.trustDomain.updated.title')}>
          {check.previous
            ? t('sites.test.trustDomain.updated.message', {
                observed: check.observed ?? '',
                previous: check.previous,
              })
            : t('sites.test.trustDomain.updated.first', { observed: check.observed ?? '' })}
          {check.identities > 0 ? (
            <>
              {/* O espaço é explícito: sem ele, o texto colaria na frase seguinte (lição do textContent). */}{' '}
              {tn('sites.test.trustDomain.updated.identities', check.identities)}{' '}
              <Link to="/identidades">{t('sites.test.trustDomain.updated.link')}</Link>
            </>
          ) : null}
        </Alert>
      ) : null}

      {check.observed === null ? (
        <p className={s.testBody}>{notReadText(check.expected, when)}</p>
      ) : null}
    </section>
  );
}

/** O «não lido»: o executor não respondeu, e o site continua com o valor de antes, quando há um. */
function notReadText(current: string, when: string | null): string {
  if (!current) return t('sites.test.trustDomain.notChecked.messageEmpty');
  if (when) return t('sites.test.trustDomain.notChecked.messageAt', { current, when });
  return t('sites.test.trustDomain.notChecked.message', { current });
}

function TrustDomainBadge({ check }: { check: SiteConnectionTest['trust_domain'] }) {
  if (check.matches) return <Badge tone="success">{t('sites.test.trustDomain.matches')}</Badge>;
  if (check.updated) return <Badge tone="warning">{t('sites.test.trustDomain.updated')}</Badge>;
  return <Badge tone="neutral">{t('sites.test.trustDomain.notChecked')}</Badge>;
}

function ProbeBadge({ result }: { result: SiteConnectionTest }) {
  // `probed: false` é «não havia o que sondar» (site sem endpoint), e é diferente
  // de «sondei e não respondeu» — que é `probed: true, reachable: false`. Um
  // semáforo de dois estados apagaria a distinção, e ela é a que diz ao operador
  // se falta configurar ou se o cluster está fora.
  if (!result.probed) return <Badge tone="neutral">{t('sites.test.probe.notProbed')}</Badge>;
  if (result.reachable) return <Badge tone="success">{t('sites.test.probe.reachable')}</Badge>;
  return <Badge tone="danger">{t('sites.test.probe.unreachable')}</Badge>;
}

function probeText(result: SiteConnectionTest): string {
  if (!result.probed) return t('sites.test.probe.notProbed.message');
  if (result.reachable) return t('sites.test.probe.reachable.message');
  // O código do executor vai **intacto** para a tela: é o mesmo vocabulário do
  // envelope (`pdp_inalcancavel`, `middleware_fora`, …), e traduzi-lo criaria um
  // segundo nome para o mesmo erro.
  return t('sites.test.probe.unreachable.message', { code: result.code ?? '' });
}
